import { useMemo } from "react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@aircall/ds";
import { EllipsisVertical } from "@aircall/react-icons";
import { useForm } from "@tanstack/react-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import { useCcAccess } from "@/hooks/useCcAccess/useCcAccess";
import { useDeleteEvaluation } from "@/hooks/useDeleteEvaluation/useDeleteEvaluation";
import { EvaluationAccordionHeaderContent } from "../EvaluationAccordionHeaderContent";

import { useUpdateEvaluation } from "../../hooks/useUpdateEvaluation/useUpdateEvaluation";
import {
  EvaluationUpdateFormBinaryQuestion,
  EvaluationUpdateFormRatingQuestion,
} from "./components/EvaluationFormQuestion";
import { EvaluationUpdateFormFooter } from "./components/EvaluationUpdateFormFooter";
import { EvaluationUpdateFormProvider } from "./contexts/EvaluationUpdateForm/EvaluationUpdateFormContext";

import { ConfirmationModal } from "@/components/ConfirmationModal";
import { useBoolean } from "@/hooks/useBoolean/useBoolean";

import {
  EvaluationUpdateFormProps,
  UpdateEvaluationOverride,
  initRatingScales,
  initSet,
  isBinaryEvaluationQuestion,
  isRatingEvaluationQuestion,
  transformFormValueIntoInput,
} from ".";

export function EvaluationUpdateForm({
  evaluation,
  revieweeUserId,
}: EvaluationUpdateFormProps) {
  const navigate = useNavigate();

  const { t } = useTranslation();

  const { callId } = useParams();

  const { deleteEvaluation, loadingDeleteEvaluation } = useDeleteEvaluation();
  const { canDelete } = useCcAccess();

  const { updateEvaluation, loadingUpdateEvaluation } = useUpdateEvaluation({
    onUpdateSuccess: () => {
      navigate("../");
    },
  });

  const {
    value: isConfirmationModalOpen,
    setFalse: closeConfirmationModal,
    setTrue: openConfirmationModal,
  } = useBoolean();

  // Map answer values to custom points.
  const ratingScales = useMemo(
    () => initRatingScales(evaluation),
    [evaluation]
  );

  const form = useForm<UpdateEvaluationOverride>({
    onSubmit: async ({ value }) => {
      updateEvaluation(transformFormValueIntoInput(value));
    },
    defaultValues: {
      categories: evaluation.categories,
      feedback: evaluation.feedback ?? "",
      // Below are hidden fields.
      revieweeUserId,
      ID: evaluation.ID,
    },
  });

  // Accordion: every category starts expanded, multiple can stay open.
  const defaultOpenedItems = useMemo(
    () => initSet(form.state.values.categories) ?? [],
    [form.state.values.categories]
  );

  const cancelHandler = () => {
    navigate("../");
  };

  const removeEvaluationHandler = async () => {
    if (callId) {
      await deleteEvaluation({ callID: callId });
    }
  };

  return (
    <>
      <EvaluationUpdateFormProvider form={form} ratingScales={ratingScales}>
        <div className="flex flex-col gap-2 py-3">
          <div
            className="flex items-center justify-between border-b border-border px-4 py-1"
            data-test="edit-evaluation"
          >
            <p className="text-xl font-bold">{evaluation.scoreCardName}</p>
            {canDelete && (
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={<Button variant="ghost" size="icon-sm" />}
                >
                  <EllipsisVertical className="size-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={openConfirmationModal}>
                    {t("evaluation.remove")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
          >
            <div className="flex flex-col gap-6">
              <div className="bg-card">
                <Accordion multiple defaultValue={defaultOpenedItems}>
                  {form.state.values.categories.map(
                    (category, categoryIndex) => (
                      <AccordionItem
                        key={category.ID}
                        value={category.ID}
                        data-test={`accordion-item-${category.ID}`}
                        className="border-b border-border"
                      >
                        <AccordionTrigger
                          data-test={`accordion-header-${category.ID}`}
                          className="cursor-pointer"
                        >
                          {/* We could put the remaining number of questions in the header */}
                          <EvaluationAccordionHeaderContent
                            title={category.categoryName}
                          />
                        </AccordionTrigger>
                        <AccordionContent
                          keepMounted
                          data-test={`accordion-body-${category.ID}`}
                        >
                          <div className="flex flex-col gap-10 p-4">
                            {form.state.values.categories[
                              categoryIndex
                            ].questions.map((question, questionIndex) => {
                              if (isRatingEvaluationQuestion(question)) {
                                return (
                                  <EvaluationUpdateFormRatingQuestion
                                    key={`${category.categoryOrder}-${questionIndex}`}
                                    order={question.questionOrder + 1}
                                    text={question.questionText}
                                    possibleAnswers={
                                      question.questionPossibleAnswers
                                    }
                                    categoryIndex={categoryIndex}
                                    questionIndex={questionIndex}
                                  />
                                );
                              }

                              if (isBinaryEvaluationQuestion(question)) {
                                return (
                                  <EvaluationUpdateFormBinaryQuestion
                                    key={`${category.categoryOrder}-${questionIndex}`}
                                    order={question.questionOrder + 1}
                                    text={question.questionText}
                                    possibleAnswers={
                                      question.questionPossibleAnswers
                                    }
                                    categoryIndex={categoryIndex}
                                    questionIndex={questionIndex}
                                  />
                                );
                              }

                              throw new Error("Unknown question type.");
                            })}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    )
                  )}
                </Accordion>
              </div>
              <EvaluationUpdateFormFooter
                loading={loadingUpdateEvaluation}
                onClickCancel={cancelHandler}
              />
            </div>
          </form>
        </div>
      </EvaluationUpdateFormProvider>
      <ConfirmationModal
        onHide={closeConfirmationModal}
        show={isConfirmationModalOpen}
        loading={loadingDeleteEvaluation}
        actions={[
          {
            label: t("cancel"),
            onClick: closeConfirmationModal,
          },
          {
            variant: "destructive",
            label: t("evaluation.remove_it"),
            onClick: removeEvaluationHandler,
          },
        ]}
        title={t("evaluation.remove")}
        description={t("evaluation.cancel_evaluation_description", {
          scoreCardName: evaluation.scoreCardName,
        })}
      />
    </>
  );
}
